import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import App from './App';
import { FarcasterProvider } from './contexts/FarcasterContext';
import { NFTProvider } from './contexts/NFTContext';

import './index.css';

/**
 * CRITICAL: Main entry point for Castoria NFT Marketplace.
 * 
 * Simplified provider hierarchy for Farcaster Quick Auth:
 * 1. QueryClient - For all API calls and caching
 * 2. FarcasterProvider - For Farcaster social features with Quick Auth
 * 3. NFTProvider - For NFT marketplace functionality
 * 4. BrowserRouter - For client-side routing
 * 
 * NOTE: WalletConnect/RainbowKit removed - using Farcaster Quick Auth instead
 */

// Create QueryClient with sensible defaults for NFT marketplace
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
      retry: 3,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: 1,
    },
  },
});

// Get the root element and render the app
const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element not found. This should never happen in production.');
}

const root = createRoot(container);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <FarcasterProvider>
        <NFTProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </NFTProvider>
      </FarcasterProvider>
    </QueryClientProvider>
  </React.StrictMode>
); 